import React, { useEffect, useState } from "react";
import { PulseLoader } from "react-spinners";
import { Trash2, X } from "lucide-react";

const DeleteModal = ({
  isOpen = false,
  toggleModal,
  handleDelete,
  activeDeleteId,
  setSpinner,
  name,
  alertText = "",
}) => {
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setLoading(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 overflow-x-hidden overflow-y-auto outline-none focus:outline-none">
      {/* Backdrop with blur effect */}
      <div 
        className="fixed inset-0 bg-slate-100/10 transition-opacity duration-300" 
        onClick={() => {
          if (!loading) {
            toggleModal(false);
            setSpinner(false);
          }
        }}
      />

      <div className="relative w-full max-w-md mx-auto my-6 animate-scaleIn">
        <div className="relative flex flex-col w-full bg-white border-0 rounded-2xl shadow-2xl outline-none focus:outline-none overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-gray-50/50">
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-[0.1em]">
              Delete Confirmation {name ? ` | ${String(name)}` : ""} {activeDeleteId ? ` | #${String(activeDeleteId)}` : ""}
            </h3>
            <button
              className="p-1 ml-auto bg-transparent border-0 text-gray-400 hover:text-gray-600 transition-colors focus:outline-none"
              onClick={() => {
                toggleModal(false);
                setSpinner(false);
              }}
            >
              <X size={18} />
            </button>
          </div>
          <div className="relative p-8 flex-auto">
            {alertText && (
              <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg flex items-start space-x-3">
                <div className="mt-0.5">
                  <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <p className="text-sm font-bold text-red-800 leading-tight">{alertText}</p>
              </div>
            )}

            <div className="text-center">
              <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-red-100 mb-3">
                <Trash2 className="h-10 w-10 text-red-600" aria-hidden="true" />
              </div>
              
              <h4 className="text-2xl font-black text-gray-900 mb-3">Are you sure?</h4>
              <p className="text-gray-500 text-base leading-relaxed px-4">
                This item will be permanently removed. This action cannot be undone.
              </p>
            </div>
          </div>
          <div className="flex items-center justify-center p-6 pt-2 gap-3">
            <button
              className="flex-1 px-4 py-2 border-2 border-gray-100 text-gray-500 bg-white rounded-xl font-bold hover:bg-gray-50 hover:text-gray-700 transition-all active:scale-95 focus:outline-none"
              type="button"
              onClick={() => {
                toggleModal(false);
                setSpinner(false);
              }}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              className="flex-1 px-4 py-3 bg-[#0F1E3B] text-white rounded-xl font-bold shadow-[0_10px_20px_-10px_rgba(15,30,59,0.5)] hover:bg-[#1a2e5a] hover:shadow-lg transition-all active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center min-h-[48px]"
              type="button"
              onClick={() => {
                handleDelete();
                setLoading(true);
                setSpinner(false);
              }}
              disabled={loading}
            >
              {loading ? (
                <PulseLoader color="#FFFFFF" size={8} margin={2} />
              ) : (
                "Yes, Delete It!"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteModal;
